import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, IdView } from "../../../../components";
import { OnlyFansAccount } from "../../../../api/staff";
import { useTranslations } from "../../../../contexts/translation.context";
import { getCreatorLevelConfig } from "../../../users/creators/constants";

interface CreatorCellProps {
  account: OnlyFansAccount;
}

/**
 * CreatorCell Component
 *
 * Renders the creator information cell with:
 * - Creator name (clickable)
 * - Creator ID (with copy functionality)
 * - Creator level badge
 *
 * Based on Figma design: Криэйтор column
 */
const CreatorCell: React.FC<CreatorCellProps> = ({ account }) => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const owner = account.owner;

  const levelConfig = useMemo(() => {
    if (!owner) return { label: '', color: '#F4F4F5' };
    return getCreatorLevelConfig(owner.lvl, t);
  }, [owner, t]);

  if (!owner) {
    return (
      <Typography variant="body3" className="text-[#71717A]">
        -
      </Typography>
    );
  }

  const handleClick = () => {
    navigate(`/users/creators/${owner.id}/balance`);
  };

  return (
    <div className="flex flex-col items-start gap-[4px]">
      <div className="flex items-center gap-[8px]">
        {/* Creator Name */}
        <Typography
          variant="body3"
          className="!text-[#006FEE] text-[14px] font-medium cursor-pointer hover:underline"
          onClick={handleClick}
        >
          {owner.name}
        </Typography>

        {/* Level Badge */}
        <span
          className="inline-flex h-[24px] items-center justify-center rounded-lg px-[8px] text-[12px] font-medium capitalize"
          style={{
            backgroundColor: levelConfig.color,
            color: '#FFFFFF'
          }}
        >
          {levelConfig.label}
        </span>
      </div>

      {/* Creator ID */}
      <IdView textToCopy={owner.id}>
        {owner.id.slice(-4)}
      </IdView>
    </div>
  );
};

export default CreatorCell;
